import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { auth } from './firebase';
import { GoogleAuthProvider, signInWithPopup, createUserWithEmailAndPassword, sendEmailVerification } from "firebase/auth";
import config_names from './config_names';
import { 
  FiMail, 
  FiAlertCircle, 
  FiArrowRight, 
  FiArrowLeft,
  FiCheckCircle
} from 'react-icons/fi';
import { FaGoogle } from 'react-icons/fa';
import './auth-pages.css';

function SignupPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [showEmailSignup, setShowEmailSignup] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const navigate = useNavigate();

  const getErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/weak-password':
        return 'The password is too weak. Please use a stronger password.';
      case 'auth/email-already-in-use':
        return 'This email is already in use. Please use a different email.';
      case 'auth/invalid-email':
        return 'The email address is invalid. Please enter a valid email address.';
      default:
        return 'An unexpected error occurred. Please try again.';
    }
  };

  const handleGoogleSignup = () => {
    const provider = new GoogleAuthProvider();
    signInWithPopup(auth, provider)
      .then((result) => {
        navigate('/dashboard');
      })
      .catch((error) => {
        setError(getErrorMessage(error.code));
      });
  };

  const handleSkip = () => {
    navigate('/dashboard');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError(null);
  
    if (password.length < 8) {
      setError("Password must be at least 8 characters long.");
      return;
    }
  
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      await sendEmailVerification(userCredential.user);
      setIsSuccess(true);
      setTimeout(() => {
        navigate('/dashboard');
      }, 2000);
    } catch (error) {
      setError(getErrorMessage(error.code));
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Create Account</h1>
          <p>Get started with {config_names.projectName}</p>
        </div>

        {error && (
          <div className="auth-error">
            <FiAlertCircle />
            <span>{error}</span>
          </div>
        )}

        {isSuccess && (
          <div className="auth-success">
            <FiCheckCircle />
            <span>Account created successfully! Redirecting to dashboard...</span>
          </div>
        )}

        {!showEmailSignup && !isSuccess ? (
          <>
            {config_names.allow_skip_login_for_now && (
              <button 
                className="auth-btn secondary-btn" 
                onClick={handleSkip}
              >
                <FiArrowRight /> Skip for now
              </button>
            )}
            
            <button 
              className="auth-btn google-btn" 
              onClick={handleGoogleSignup}
            >
              <FaGoogle /> Sign up with Google
            </button>
            
            <div className="auth-divider">or</div>
            
            <button 
              className="auth-btn primary-btn" 
              onClick={() => setShowEmailSignup(true)}
            >
              <FiMail /> Sign up with Email
            </button>
          </>
        ) : !isSuccess ? (
          <form className="auth-form" onSubmit={handleSubmit}>
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="email" 
                  className="form-control" 
                  placeholder="Email" 
                  value={email} 
                  onChange={(e) => setEmail(e.target.value)} 
                  required 
                />
              </div>
            </div>
            
            <div className="form-group">
              <div className="input-icon-wrapper">
                <input 
                  type="password" 
                  className="form-control" 
                  placeholder="Password (min. 8 characters)" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)} 
                  required 
                />
              </div>
            </div>
            
            <button type="submit" className="auth-btn primary-btn">
              <FiArrowRight /> Create Account
            </button>
            
            <button 
              type="button" 
              className="auth-btn secondary-btn" 
              onClick={() => setShowEmailSignup(false)}
            >
              <FiArrowLeft /> Back
            </button>
          </form>
        ) : null}

        {!isSuccess && (
          <div className="auth-footer">
            <span>Already have an account? </span>
            <Link to="/login">Sign in</Link>
          </div>
        )}

        <div className="auth-legal">
          By continuing, you acknowledge that you accept {config_names.projectName_genitive}{' '}
          <a href={`${config_names.domain}/terms`}>Terms of Use</a> and{' '}
          <a href={`${config_names.domain}/privacy`}>Privacy Policy</a>.
        </div>
      </div>
    </div>
  );
}

export default SignupPage;